'use client';

import { Icon, type IconName } from '~/components/primitives';
import type { Activity, ActivityTraits } from '@funkey/types/graphql/graphql';
import type { I18n } from '@funkey/locales';
import { cx } from 'class-variance-authority';
import { useI18n } from '~/features/i18n';
import Title from '~/components/utils/Title';

export default function ActivityPageTraits(props: ActivityTraits) {
    const i18n = useI18n();
    return (
        <section className="mb-15">
            <Title as="h3" size="xl" weight="bold" className="mb-3">
                {i18n._('activity.details.traits.title')}
            </Title>
            <p className="mb-4">{i18n._('activity.details.traits.description')}</p>
            <ul className="grid grid-cols-[auto_1fr] items-center gap-x-4">
                {traits(i18n).map(trait => {
                    const score = props[trait.name] ?? 0;
                    return (
                        <li key={trait.name} className="contents">
                            <div className="my-3 flex items-center gap-3 text-primary">
                                <Icon
                                    size="xl"
                                    viewBox="0 0 48 48"
                                    strokeWidth={2}
                                    name={trait.icon}
                                    className={trait.color}
                                />
                                <span className="text-sm md:text-base">{trait.label}</span>
                            </div>
                            <div className="h-3 w-full rounded-full bg-white">
                                <div
                                    className={cx(
                                        'relative h-full rounded-full bg-secondary',
                                        scoreClass(score),
                                    )}
                                >
                                    <progress
                                        id={trait.name}
                                        value={scoreValue(score)}
                                        max={100}
                                        className="sr-only appearance-none"
                                    />
                                    <label
                                        htmlFor={trait.name}
                                        className={cx(
                                            !score
                                                ? 'left-0 rounded-full bg-blue-300'
                                                : 'right-0 bg-secondary px-3 py-2',
                                            'absolute top-1/2 -translate-y-1/2 rounded-full text-xs font-medium text-white',
                                        )}
                                    >
                                        {score ? (
                                            scoreLabel(score, i18n)
                                        ) : (
                                            <Icon name="cross" className="text-white" />
                                        )}
                                    </label>
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

const scoreValue = (score: number): number => {
    if (!score) return 0;

    const value = (100 / 3) * score;

    return Math.floor(value);
};

const scoreClass = (score: number) => {
    if (!score) return 'w-0';
    if (score === 1) return 'w-1/3';
    if (score === 2) return 'w-2/3';
    return 'w-full';
};

const scoreLabel = (score: number, i18n: I18n) => {
    if (!score) return null;
    if (score === 1) return i18n._('Low');
    if (score === 2) return i18n._('Moderate');
    return i18n._('High');
};

const traits = (i18n: I18n) =>
    [
        {
            name: 'teamwork',
            label: i18n._('activity.details.traits.teamwork'),
            // label: i18n._('Teamwork'),
            icon: 'teamwork',
            color: cx('text-secondary'),
        },
        {
            name: 'competition',
            label: i18n._('activity.details.traits.competition'),
            // label: i18n._('Competition'),
            icon: 'competition',
            color: cx('text-tertiary'),
        },
        {
            name: 'action',
            label: i18n._('activity.details.traits.action'),
            // label: i18n._('Action'),
            icon: 'action',
            color: cx('text-brown-900'),
        },
        {
            name: 'learning',
            label: i18n._('activity.details.traits.learning'),
            // label: i18n._('Learning'),
            icon: 'learning',
            color: cx('text-yellow-600'),
        },
    ] as const satisfies {
        name: keyof Activity['traits'];
        label: string;
        icon: IconName;
        color: string;
    }[];
